import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const login = (email, password) => api.post('/auth/login', { email, password });
export const register = (data) => api.post('/auth/register', data);
export const getMe = () => api.get('/auth/me');
export const requestResetPassword = (email) => api.post('/auth/request-reset-password', { email });
export const confirmResetPassword = (token, new_password) => api.post('/auth/reset-password', { token, new_password });

// KPI
export const getKPI = () => api.get('/kpi');

// Agente Dashboard
export const getAgenteDashboard = () => api.get('/agente/dashboard');
export const getAgenteCommissioni = () => api.get('/agente/commissioni');
export const getAgenteAziende = () => api.get('/agente/aziende');

// Azienda Dashboard  
export const getAziendaDashboard = () => api.get('/azienda/dashboard');
export const getAziendaElogi = () => api.get('/azienda/elogi');
export const getAziendaQR = () => api.get('/azienda/qr');

// Aziende
export const getAziende = () => api.get('/aziende');
export const getAzienda = (id) => api.get(`/aziende/${id}`);
export const createAzienda = (data) => api.post('/aziende', data);
export const updateAzienda = (id, data) => api.put(`/aziende/${id}`, data);
export const toggleAzienda = (id) => api.patch(`/aziende/${id}/toggle`);
export const deleteAzienda = (id) => api.delete(`/aziende/${id}`);
export const sendAziendaWelcomeEmail = (id, password = null) => {
  const url = password 
    ? `/aziende/${id}/send-welcome-email?password=${encodeURIComponent(password)}`
    : `/aziende/${id}/send-welcome-email`;
  return api.post(url);
};
export const changeAziendaPassword = (id, password, sendEmail = false) => 
  api.post(`/aziende/${id}/change-password?password=${encodeURIComponent(password)}&send_email=${sendEmail}`);

// Users
export const getUsers = (ruolo) => api.get('/users', { params: { ruolo } });
export const createUserAzienda = (data) => api.post('/users/azienda', data);
export const createUserAgente = (data) => api.post('/users/agente', data);
export const deleteUser = (userId) => api.delete(`/users/${userId}`);

// Elogi
export const getElogi = (origine) => api.get('/elogi', { params: { origine } });
export const getElogio = (id) => api.get(`/elogi/${id}`);
export const createElogio = (data) => api.post('/elogi', data);
export const updateElogio = (id, data) => api.put(`/elogi/${id}`, data);
export const deleteElogio = (id) => api.delete(`/elogi/${id}`);
export const generaTestoElogio = (id_elogio) => api.post('/elogi/genera-testo', { id_elogio });
export const syncElogioWix = (id_elogio) => api.post(`/elogi/${id_elogio}/sync-wix`);
export const getPDFElogio = (id_elogio) => `${API_URL}/elogi/${id_elogio}/pdf`;

// Download PDF with authentication
export const downloadPDFElogio = async (id_elogio, nome, cognome) => {
  const response = await api.get(`/elogi/${id_elogio}/pdf`, {
    responseType: 'blob'
  });
  
  // Create download link
  const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = `Elogio_${nome}_${cognome}.pdf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

// Links/QR
export const getLinks = (params) => api.get('/links', { params });
export const assegnaLink = (linkId, idElogio) => {
  const formData = new FormData();
  formData.append('id_elogio', idElogio);
  return api.post(`/links/${linkId}/assegna`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};
export const attivaLink = (linkId) => api.post(`/links/${linkId}/attiva`);
export const getQRCode = (linkId) => `${API_URL}/links/${linkId}/qr`;
export const aggiungiLinks = (idAzienda, rangeInizio, rangeFine) => {
  const formData = new FormData();
  formData.append('id_azienda', idAzienda);
  formData.append('range_inizio', rangeInizio);
  formData.append('range_fine', rangeFine);
  return api.post('/links/aggiungi', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};
export const getAziendaQRRanges = (idAzienda) => api.get(`/aziende/${idAzienda}/qr-ranges`);
export const rimuoviQRDaAzienda = (idAzienda, qrStart, qrEnd) => 
  api.delete(`/aziende/${idAzienda}/qr-ranges?qr_start=${qrStart}&qr_end=${qrEnd}`);

// Agenti
export const getAgenti = () => api.get('/agenti');
export const createAgente = (data) => api.post('/agenti', data);
export const updateAgente = (id, data) => api.put(`/agenti/${id}`, data);
export const deleteAgente = (id) => api.delete(`/agenti/${id}`);
export const sendAgenteWelcomeEmail = (id, password = null) => {
  const url = password 
    ? `/agenti/${id}/send-welcome-email?password=${encodeURIComponent(password)}`
    : `/agenti/${id}/send-welcome-email`;
  return api.post(url);
};
export const getElogiAgente = (id) => api.get(`/agenti/${id}/elogi`);

// Ordini e Pagamenti
export const getOrdini = () => api.get('/ordini');
export const getOrdineDetail = (idOrdine) => api.get(`/ordini/${idOrdine}`);
export const deleteOrdine = (idOrdine) => api.delete(`/ordini/${idOrdine}`);
export const getPrezzi = () => api.get('/prezzi');
export const creaOrdine = (idElogio) => api.post(`/ordini/crea?id_elogio=${idElogio}`);
export const creaStripeCheckout = (idOrdine) => api.post(`/ordini/${idOrdine}/stripe-checkout`);
export const creaPayPalOrder = (idOrdine) => api.post(`/ordini/${idOrdine}/paypal-create`);
export const confermaPagamentoManuale = (idOrdine) => api.post(`/ordini/${idOrdine}/conferma-pagamento`);
export const attivaElogioGratuito = (idElogio) => api.post(`/elogi/${idElogio}/attiva-gratuito`);

// Profilo Azienda
export const getProfilo = () => api.get('/profilo');

// Settings
export const getSettings = () => api.get('/settings');
export const updateSettings = (data) => api.put('/settings', data);
export const resetEmailTemplates = () => api.post('/settings/reset-email-templates');
export const uploadFavicon = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/settings/favicon', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

// Sync
export const syncToWix = (idElogio) => api.post(`/sync/wix/${idElogio}`);

// Admin - Gestione QR
export const getDiagnosticaQR = () => api.get('/admin/qr-diagnostica');
export const pulisciQROrfani = () => api.delete('/admin/qr-orfani');
export const rimuoviQRPerRange = (qrStart, qrEnd) => 
  api.delete(`/admin/qr-by-range?qr_start=${qrStart}&qr_end=${qrEnd}`);
export const debugElogio = (idElogio) => api.get(`/admin/debug-elogio/${idElogio}`);
export const collegaElogioAzienda = (idElogio, idAzienda) => 
  api.post(`/admin/collega-elogio-azienda?id_elogio=${idElogio}&id_azienda=${idAzienda}`);
export const diagnosticaAziendaElogi = (idAzienda) => api.get(`/admin/diagnostica-azienda/${idAzienda}`);

// Init
export const initAdmin = () => api.post('/init-admin');

// File upload
export const uploadFile = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

// Galleria Elogio
export const uploadGalleriaImage = (idElogio, file, isPrincipale = false) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('is_principale', isPrincipale);
  return api.post(`/elogi/${idElogio}/galleria`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

export const deleteGalleriaImage = (idElogio, fileId) => 
  api.delete(`/elogi/${idElogio}/galleria/${fileId}`);

export const getGalleria = (idElogio) => api.get(`/elogi/${idElogio}/galleria`);

export const getFileUrl = (fileId) => `${API_URL}/files/${fileId}`;

// Email
export const sendNotificationEmail = (toEmail, tipo, idElogio = null, idAzienda = null) => 
  api.post('/email/send', { to_email: toEmail, tipo, id_elogio: idElogio, id_azienda: idAzienda });

export const testEmail = (toEmail) => {
  const formData = new FormData();
  formData.append('to_email', toEmail);
  return api.post('/email/test', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

export default api;
